import { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { Button, FormField, Input, Textarea, Switch } from '@/components';
import { CurrencySelect } from '../../components/CurrencySelect';
import { useCompanyPreferences } from '../../hooks/useCompanyPreferences';
import {
  useCreateQuotation,
  useUpdateQuotation,
  useQuotationPdfTemplates,
} from '../hooks';
import {
  DEFAULT_PDF_TEMPLATE_KEY,
  FIELD_LABEL_CLASS,
  normalizeDeliveryCharge,
  sanitizeDeliveryChargeInput,
} from '../utils';
import { parseApiError } from '@/common/utils';
import { QuotationTemplateSelectModal } from './QuotationTemplateSelectModal';
import { TemplatePdfThumbnail } from './TemplatePdfThumbnail';

const EMPTY_FORM = {
  title: '',
  quote_code: '',
  description: '',
  currency: 'INR',
  tax_rate: '',
  delivery_term: '',
  delivery_charge: '',
  terms_and_conditions: '',
  notes: '',
  is_active: true,
  show_price: true,
  template_key: DEFAULT_PDF_TEMPLATE_KEY,
};

const TAX_PRESETS = ['0', '5', '12', '18', '28'];

function FieldHint({ children }) {
  return <p className="text-[11px] text-text-muted leading-snug">{children}</p>;
}

function normalizeTaxRate(value) {
  if (value === '' || value == null) return '0.00';
  const n = Number(value);
  if (Number.isNaN(n) || n < 0) return null;
  return Math.min(100, n).toFixed(2);
}

/**
 * Create/Edit quotation template form (full-page body — no drawer).
 */
export function QuotationForm({
  companyId,
  quotation,
  onSuccess,
  onCancel,
  initialTemplateKey,
  formId = 'quotation-form',
}) {
  const isEditing = !!quotation?.id;
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [isChangeTemplateOpen, setIsChangeTemplateOpen] = useState(false);

  const { preferences } = useCompanyPreferences(companyId);
  const { data: templatesData } = useQuotationPdfTemplates(companyId);

  const templates = useMemo(() => {
    const list = Array.isArray(templatesData)
      ? templatesData
      : templatesData?.results || [];
    return list;
  }, [templatesData]);

  const selectedTemplate = useMemo(() => {
    return templates.find((t) => t.key === form.template_key);
  }, [templates, form.template_key]);

  const createMutation = useCreateQuotation();
  const updateMutation = useUpdateQuotation();
  const isPending = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    setErrors({});
    if (quotation) {
      setForm({
        title: quotation.title || '',
        quote_code: quotation.quote_code || '',
        description: quotation.description || '',
        currency: quotation.currency || 'INR',
        tax_rate: quotation.tax_rate != null ? String(quotation.tax_rate) : '',
        delivery_term: quotation.delivery_term || '',
        delivery_charge:
          quotation.delivery_charge != null &&
          quotation.delivery_charge !== '' &&
          Number(quotation.delivery_charge) !== 0
            ? String(quotation.delivery_charge)
            : '',
        terms_and_conditions: quotation.terms_and_conditions || '',
        notes: quotation.notes || '',
        is_active: quotation.is_active !== false,
        show_price: quotation.show_price !== false,
        template_key:
          quotation.template_key ||
          initialTemplateKey ||
          DEFAULT_PDF_TEMPLATE_KEY,
      });
      return;
    }
    setForm({
      ...EMPTY_FORM,
      currency: preferences?.currency || 'INR',
      template_key: initialTemplateKey || DEFAULT_PDF_TEMPLATE_KEY,
    });
  }, [quotation, preferences?.currency, initialTemplateKey]);

  const set = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    if (errors[field]) {
      setErrors((p) => {
        const next = { ...p };
        delete next[field];
        return next;
      });
    }
  };

  const validate = () => {
    const next = {};
    const title = form.title.trim();
    if (!title) next.title = 'Title is required.';
    else if (title.length > 255)
      next.title = 'Title must be 255 characters or less.';

    if (form.quote_code.length > 255) {
      next.quote_code = 'Quote code must be 255 characters or less.';
    }

    if (!form.currency) next.currency = 'Select a currency.';

    if (form.tax_rate !== '') {
      const n = Number(form.tax_rate);
      if (Number.isNaN(n) || n < 0 || n > 100) {
        next.tax_rate = 'Enter a tax rate between 0 and 100.';
      }
    }

    if (form.delivery_term.length > 255) {
      next.delivery_term = 'Delivery terms must be 255 characters or less.';
    }
    if (form.delivery_charge !== '') {
      const charge = Number(form.delivery_charge);
      if (Number.isNaN(charge) || charge < 0) {
        next.delivery_charge = 'Enter a delivery amount of 0 or more.';
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const action = e.nativeEvent?.submitter?.value;
    const isDraft = action === 'draft';

    if (!validate()) return;

    const tax_rate = normalizeTaxRate(form.tax_rate);
    if (tax_rate == null) {
      setErrors((p) => ({ ...p, tax_rate: 'Enter a valid tax rate.' }));
      return;
    }
    const delivery_charge = normalizeDeliveryCharge(form.delivery_charge);
    if (delivery_charge == null) {
      setErrors((p) => ({
        ...p,
        delivery_charge: 'Enter a valid delivery amount.',
      }));
      return;
    }

    const payload = {
      title: form.title.trim(),
      quote_code: form.quote_code.trim(),
      description: form.description.trim(),
      currency: form.currency,
      tax_rate,
      delivery_term: form.delivery_term.trim(),
      delivery_charge,
      terms_and_conditions: form.terms_and_conditions.trim(),
      notes: form.notes.trim(),
      is_active: isDraft ? false : form.is_active,
      show_price: form.show_price !== false,
      template_key: form.template_key || DEFAULT_PDF_TEMPLATE_KEY,
    };

    const onError = (err) => {
      const { message, fieldErrors } = parseApiError(err);
      if (fieldErrors && Object.keys(fieldErrors).length) {
        setErrors(fieldErrors);
      }
      toast.error(message || 'Failed to save quotation.');
    };

    if (isEditing) {
      updateMutation.mutate(
        { companyId, quotationId: quotation.id, payload },
        {
          onSuccess: (data) => {
            toast.success('Quotation updated.');
            onSuccess?.(data || quotation);
          },
          onError,
        },
      );
      return;
    }

    createMutation.mutate(
      { companyId, payload },
      {
        onSuccess: (data) => {
          toast.success('Quotation created. Add products next.');
          onSuccess?.(data);
        },
        onError,
      },
    );
  };

  const taxPresetActive = (preset) => {
    if (form.tax_rate === '') return preset === '0';
    return Number(form.tax_rate) === Number(preset);
  };

  return (
    <>
      <form
        id={formId}
        onSubmit={handleSubmit}
        noValidate
        className="w-full flex flex-col gap-6"
      >
        {/* Selected PDF Template Summary Banner */}
        <div className="rounded-xl border border-border-default bg-[#F8FAF8] p-3.5 sm:p-4 flex items-center justify-between gap-3 shadow-2xs">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-14 h-10 rounded-lg overflow-hidden border border-border-default bg-white shrink-0 relative">
              <TemplatePdfThumbnail
                companyId={companyId}
                template={selectedTemplate}
              />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-medium text-text-muted uppercase tracking-wider">
                  PDF Template
                </span>
                {selectedTemplate?.category && (
                  <span className="text-[10px] font-medium px-1.5 py-0.2 rounded-full bg-primary/10 text-primary">
                    {selectedTemplate.category}
                  </span>
                )}
              </div>
              <p className="text-sm font-semibold text-text-primary truncate">
                {selectedTemplate?.name ||
                  (form.template_key
                    ? String(form.template_key).replace(/_/g, ' ')
                    : 'Default Design')}
              </p>
              {selectedTemplate?.theme_config && (
                <div className="flex items-center gap-1 mt-1">
                  {[
                    selectedTemplate.theme_config.primary_color,
                    selectedTemplate.theme_config.accent_color,
                    selectedTemplate.theme_config.table_header_bg,
                  ]
                    .filter(Boolean)
                    .map((col, idx) => (
                      <span
                        key={idx}
                        className="w-2.5 h-2.5 rounded-full border border-black/10 shrink-0"
                        style={{ backgroundColor: col }}
                      />
                    ))}
                </div>
              )}
            </div>
          </div>

          <Button
            type="button"
            variant="secondary"
            onClick={() => setIsChangeTemplateOpen(true)}
            disabled={isPending}
            className="shrink-0 h-8 px-3 text-xs flex items-center gap-1.5"
          >
            <svg
              className="w-3.5 h-3.5 text-text-secondary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span>Change Template</span>
          </Button>
        </div>

        <section className="flex flex-col gap-3">
          <h3 className="text-h6 font-bold text-text-primary tracking-wide border-b border-border-default pb-2">
            Basics
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5">
            <FormField
              label="Title"
              htmlFor="quotation-title"
              required
              error={errors.title}
              labelClassName={FIELD_LABEL_CLASS}
            >
              <Input
                id="quotation-title"
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
                placeholder="e.g. Summer Catalog 2026"
                maxLength={255}
                error={Boolean(errors.title)}
                autoFocus
                className="!bg-white"
              />
              <FieldHint>
                Name shown on the quote sent to the customer.
              </FieldHint>
            </FormField>

            <FormField
              label="Quote Code"
              htmlFor="quotation-code"
              error={errors.quote_code}
              labelClassName={FIELD_LABEL_CLASS}
            >
              <Input
                id="quotation-code"
                value={form.quote_code}
                onChange={(e) => set('quote_code', e.target.value)}
                placeholder="e.g. QT-2026-SUMMER"
                maxLength={255}
                error={Boolean(errors.quote_code)}
                className="!bg-white"
              />
              <FieldHint>
                Optional short ID. Helps pick this template in Automations.
              </FieldHint>
            </FormField>

            <FormField
              label="Description"
              htmlFor="quotation-description"
              labelClassName={FIELD_LABEL_CLASS}
              className="md:col-span-2"
            >
              <Textarea
                id="quotation-description"
                rows={2}
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                placeholder="What this catalog is for…"
                className="min-h-[72px] resize-y !bg-white"
              />
            </FormField>
          </div>
        </section>

        <section className="flex flex-col gap-3">
          <h3 className="text-h6 font-bold text-text-primary tracking-wide border-b border-border-default pb-2">
            Pricing
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-5">
            <FormField
              label="Currency"
              required
              error={errors.currency}
              labelClassName={FIELD_LABEL_CLASS}
            >
              <CurrencySelect
                value={form.currency}
                onChange={(value) => set('currency', value)}
                disabled={isPending}
                error={Boolean(errors.currency)}
                placeholder="Select currency"
                className="w-full h-[40px] sm:h-[44px]"
              />
              <FieldHint>Base currency for item pricing and totals.</FieldHint>
            </FormField>

            <FormField
              label="Tax Rate"
              htmlFor="quotation-tax"
              error={errors.tax_rate}
              labelClassName={FIELD_LABEL_CLASS}
            >
              <div className="relative">
                <Input
                  id="quotation-tax"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  inputMode="decimal"
                  value={form.tax_rate}
                  onChange={(e) => set('tax_rate', e.target.value)}
                  placeholder="0"
                  error={Boolean(errors.tax_rate)}
                  className="pr-10 !bg-white"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-text-muted">
                  %
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5 pt-1">
                {TAX_PRESETS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => set('tax_rate', preset)}
                    className={`h-7 px-2.5 rounded-lg text-[11px] font-medium transition-colors ${
                      taxPresetActive(preset)
                        ? 'bg-primary text-white shadow-sm'
                        : 'bg-white border border-border-default text-text-secondary hover:border-primary hover:text-primary'
                    }`}
                  >
                    {preset}%
                  </button>
                ))}
              </div>
              <FieldHint>
                Applied on product subtotal. Leave blank for 0%.
              </FieldHint>
            </FormField>

            <FormField
              label="Delivery Terms"
              htmlFor="quotation-delivery-term"
              error={errors.delivery_term}
              labelClassName={FIELD_LABEL_CLASS}
            >
              <Input
                id="quotation-delivery-term"
                value={form.delivery_term}
                onChange={(e) => set('delivery_term', e.target.value)}
                placeholder="Ex-Works, Door Delivery, F.O.R."
                maxLength={255}
                error={Boolean(errors.delivery_term)}
                className="!bg-white"
              />
              <FieldHint>
                Type any delivery label. Leave blank to hide it.
              </FieldHint>
            </FormField>

            <FormField
              label="Delivery / Freight Amount"
              htmlFor="quotation-delivery-charge"
              error={errors.delivery_charge}
              labelClassName={FIELD_LABEL_CLASS}
            >
              <Input
                id="quotation-delivery-charge"
                type="text"
                inputMode="decimal"
                value={form.delivery_charge}
                onChange={(e) =>
                  set(
                    'delivery_charge',
                    sanitizeDeliveryChargeInput(e.target.value),
                  )
                }
                placeholder="0.00"
                error={Boolean(errors.delivery_charge)}
                className="!bg-white"
              />
              <FieldHint>
                Leave empty or 0 if delivery is not charged or included in base
                price.
              </FieldHint>
            </FormField>
          </div>
        </section>

        <section className="flex flex-col gap-3">
          <h3 className="text-h6 font-bold text-text-primary tracking-wide border-b border-border-default pb-2">
            Optional Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5">
            <FormField
              label="Terms & Conditions"
              htmlFor="quotation-terms"
              labelClassName={FIELD_LABEL_CLASS}
            >
              <Textarea
                id="quotation-terms"
                rows={3}
                value={form.terms_and_conditions}
                onChange={(e) => set('terms_and_conditions', e.target.value)}
                placeholder="Payment terms, delivery, validity…"
                className="min-h-[88px] resize-y !bg-white"
              />
              <FieldHint>Included on the customer quote.</FieldHint>
            </FormField>

            <FormField
              label="Internal Notes"
              htmlFor="quotation-notes"
              labelClassName={FIELD_LABEL_CLASS}
            >
              <Textarea
                id="quotation-notes"
                rows={3}
                value={form.notes}
                onChange={(e) => set('notes', e.target.value)}
                placeholder="Team-only notes…"
                className="min-h-[88px] resize-y !bg-white"
              />
              <FieldHint>Not sent to the customer.</FieldHint>
            </FormField>
          </div>
        </section>

        <div className="flex items-center justify-between gap-3 p-3.5 sm:p-4 rounded-xl bg-[#F8FAF8] border border-border-default">
          <div className="min-w-0">
            <p className="text-[15px] font-semibold text-text-primary">
              Show Prices in Catalog PDF
            </p>
            <p className="text-xs text-text-secondary mt-0.5">
              Display product rates and prices in the catalog PDF and WhatsApp
              messages (Yes / No).
            </p>
          </div>
          <Switch
            checked={form.show_price}
            onChange={(checked) => set('show_price', checked)}
            disabled={isPending}
            aria-label="Show prices in catalog PDF"
          />
        </div>

        <div className="flex items-center justify-between gap-3 p-3.5 sm:p-4 rounded-xl bg-[#F8FAF8] border border-border-default">
          <div className="min-w-0">
            <p className="text-[15px] font-semibold text-text-primary">
              Active Status
            </p>
            <p className="text-xs text-text-secondary mt-0.5">
              Turn off to prevent linking this template to a WhatsApp flow.
            </p>
          </div>
          <Switch
            checked={form.is_active}
            onChange={(checked) => set('is_active', checked)}
            disabled={isPending}
            aria-label="Quotation active"
          />
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3 pt-2 border-t border-border-default">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isPending}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          {!isEditing && (
            <Button
              type="submit"
              name="submit_action"
              value="draft"
              variant="secondary"
              disabled={isPending}
              className="w-full sm:w-auto"
            >
              Save Draft
            </Button>
          )}
          <Button
            type="submit"
            name="submit_action"
            value="publish"
            variant="primary"
            disabled={isPending}
            className="w-full sm:w-auto"
          >
            {isPending
              ? 'Saving…'
              : isEditing
                ? 'Save Changes'
                : 'Create Quotation'}
          </Button>
        </div>
      </form>

      <QuotationTemplateSelectModal
        isOpen={isChangeTemplateOpen}
        onClose={() => setIsChangeTemplateOpen(false)}
        onSelect={(newKey) => set('template_key', newKey)}
        companyId={companyId}
        selectedKey={form.template_key}
        title="Change Quotation Template"
        description="Select a different PDF design for this quotation. All entered form data is preserved."
        confirmButtonText="Apply Template"
      />
    </>
  );
}
